import { Database } from '@/shared/types/supabase'
// =====================================================
// TYPES DE BASE DEPUIS SUPABASE
// =====================================================
export type UnitMutationRow = Database['public']['Tables']['unit_mutations']['Row']
export type UnitMutationInsert = Database['public']['Tables']['unit_mutations']['Insert']
export type UnitMutationUpdate = Database['public']['Tables']['unit_mutations']['Update']
export type UnitMutationPartyRow = Database['public']['Tables']['unit_mutation_parties']['Row']
export type UnitMutationPartyInsert = Database['public']['Tables']['unit_mutation_parties']['Insert']
export type UnitMutationPartyUpdate = Database['public']['Tables']['unit_mutation_parties']['Update']
// =====================================================
// ENUMS
// =====================================================
export type UnitMutationType = Database['public']['Enums']['unit_mutation_type']
export type UnitMutationPartyType = Database['public']['Enums']['unit_mutation_party_type']
export type UnitMutationOwnershipType = Database['public']['Enums']['unit_mutation_ownership_type']
// =====================================================
// INTERFACES ÉTENDUES
// =====================================================
/**
 * Mutation avec ses parties (anciens et nouveaux propriétaires)
 */
export interface UnitMutationWithParties extends UnitMutationRow {
  former_parties: UnitMutationPartyWithOwner[]
  new_parties: UnitMutationPartyWithOwner[]
  documents?: Array<{
    id: string
    file_name: string
    file_path: string
    file_size: number
    mime_type: string
    uploaded_by: string
    created_at: string
  }>
}
/**
 * Partie de mutation avec les infos du propriétaire
 */
export interface UnitMutationPartyWithOwner extends UnitMutationPartyRow {
  owner: {
    id: string
    first_name: string | null
    last_name: string | null
    email: string | null
  }
}
/**
 * Historique complet d'une mutation pour affichage
 */
export interface UnitMutationHistory {
  id: string
  mutation_date: string
  mutation_type: UnitMutationType
  mutation_subtype: string | null
  is_complete: boolean
  notary_name: string | null
  deed_reference: string | null
  deed_date: string | null
  sale_price: number | null
  mutation_fees: number | null
  notes: string | null
  former_owners: MutationOwnerInfo[]
  new_owners: MutationOwnerInfo[]
  created_at: string
  created_by: string | null
}
/**
 * Info propriétaire pour historique de mutation
 */
export interface MutationOwnerInfo {
  id: string
  name: string
  share_numerator: number
  share_denominator: number
  ownership_type: UnitMutationOwnershipType
}
// =====================================================
// FORMULAIRES - INPUT TYPES
// =====================================================
/**
 * Données pour créer une nouvelle mutation (formulaire)
 */
export interface CreateUnitMutationInput {
  unit_id: string
  mutation_date: string
  mutation_type: UnitMutationType
  mutation_subtype?: string
  is_complete?: boolean
  notary_name?: string
  deed_reference?: string
  deed_date?: string
  sale_price?: number
  mutation_fees?: number
  notes?: string
  former_parties: MutationPartyInput[]
  new_parties: MutationPartyInput[]
  documents?: File[] // Files to upload
}
/**
 * Partie de mutation pour formulaire
 */
export interface MutationPartyInput {
  owner_id: string
  share_numerator: number
  share_denominator: number
  ownership_type?: UnitMutationOwnershipType
}
// =====================================================
// METADATA DES TYPES DE MUTATION
// =====================================================
export interface MutationTypeMetadata {
  code: UnitMutationType
  label: string
  description: string
  category: 'onerous' | 'gratuitous' | 'matrimonial' | 'company' | 'dismemberment' | 'indivision' | 'judicial' | 'specific'
  requiresNotary: boolean
  requiresPrice: boolean
  allowsPartial: boolean
}
/**
 * Métadonnées complètes des 22 types de mutations
 */
export const MUTATION_TYPE_METADATA: Record<UnitMutationType, MutationTypeMetadata> = {
  // ===== 1. MUTATIONS À TITRE ONÉREUX =====
  sale: {
    code: 'sale',
    label: 'Vente simple',
    description: 'Vente classique : propriétaire unique → nouveau propriétaire unique',
    category: 'onerous',
    requiresNotary: true,
    requiresPrice: true,
    allowsPartial: false,
  },
  sale_indivision: {
    code: 'sale_indivision',
    label: 'Vente à plusieurs acquéreurs',
    description: 'Création d\'une indivision par vente à plusieurs acquéreurs',
    category: 'onerous',
    requiresNotary: true,
    requiresPrice: true,
    allowsPartial: false,
  },
  life_annuity: {
    code: 'life_annuity',
    label: 'Vente en viager',
    description: 'Vendeur conserve l\'usufruit, acquéreur obtient la nue-propriété',
    category: 'onerous',
    requiresNotary: true,
    requiresPrice: true,
    allowsPartial: false,
  },
  licitation: {
    code: 'licitation',
    label: 'Licitation',
    description: 'Vente judiciaire forcée pour sortir d\'indivision',
    category: 'onerous',
    requiresNotary: true,
    requiresPrice: true,
    allowsPartial: false,
  },
  exchange: {
    code: 'exchange',
    label: 'Échange de biens',
    description: 'Swap entre deux propriétaires (rare en copropriété)',
    category: 'onerous',
    requiresNotary: true,
    requiresPrice: false,
    allowsPartial: false,
  },
  // ===== 2. MUTATIONS À TITRE GRATUIT =====
  donation: {
    code: 'donation',
    label: 'Donation',
    description: 'Donation simple, donation-partage, ou avec réserve d\'usufruit',
    category: 'gratuitous',
    requiresNotary: true,
    requiresPrice: false,
    allowsPartial: false,
  },
  inheritance: {
    code: 'inheritance',
    label: 'Succession',
    description: 'Succession ab intestat ou testamentaire, avec ou sans démembrement',
    category: 'gratuitous',
    requiresNotary: true,
    requiresPrice: false,
    allowsPartial: false,
  },
  legacy: {
    code: 'legacy',
    label: 'Legs testamentaire',
    description: 'Legs universel, à titre universel ou particulier',
    category: 'gratuitous',
    requiresNotary: true,
    requiresPrice: false,
    allowsPartial: false,
  },
  // ===== 3. MUTATIONS LIÉES AU RÉGIME MATRIMONIAL =====
  marriage: {
    code: 'marriage',
    label: 'Mariage / Changement de régime',
    description: 'Acquisition pendant mariage ou changement de régime matrimonial',
    category: 'matrimonial',
    requiresNotary: true,
    requiresPrice: false,
    allowsPartial: false,
  },
  divorce: {
    code: 'divorce',
    label: 'Divorce / Dissolution PACS',
    description: 'Attribution au conjoint ou liquidation du régime matrimonial',
    category: 'matrimonial',
    requiresNotary: false,
    requiresPrice: false,
    allowsPartial: false,
  },
  // ===== 4. MUTATIONS LIÉES AUX SOCIÉTÉS =====
  company_contribution: {
    code: 'company_contribution',
    label: 'Apport à société',
    description: 'Apport d\'un bien à une SCI ou société',
    category: 'company',
    requiresNotary: true,
    requiresPrice: false,
    allowsPartial: false,
  },
  company_dissolution: {
    code: 'company_dissolution',
    label: 'Dissolution de société',
    description: 'Retour du bien aux associés suite à dissolution',
    category: 'company',
    requiresNotary: true,
    requiresPrice: false,
    allowsPartial: false,
  },
  company_share_transfer: {
    code: 'company_share_transfer',
    label: 'Cession de parts sociales',
    description: 'Changement d\'associés (mutation indirecte)',
    category: 'company',
    requiresNotary: false,
    requiresPrice: true,
    allowsPartial: true,
  },

  // ===== 5. MUTATIONS PAR DÉMEMBREMENT =====
  usufruct_end: {
    code: 'usufruct_end',
    label: 'Extinction d\'usufruit',
    description: 'Fin de l\'usufruit (décès, renonciation, terme)',
    category: 'dismemberment',
    requiresNotary: true,
    requiresPrice: false,
    allowsPartial: false,
  },
  consolidation: {
    code: 'consolidation',
    label: 'Consolidation',
    description: 'Rachat de l\'usufruit par le nu-propriétaire',
    category: 'dismemberment',
    requiresNotary: true,
    requiresPrice: true,
    allowsPartial: false,
  },

  // ===== 6. MUTATIONS LIÉES À L'INDIVISION =====
  share_transfer: {
    code: 'share_transfer',
    label: 'Cession de quote-part',
    description: 'Un indivisaire vend sa part à un tiers ou à un co-indivisaire',
    category: 'indivision',
    requiresNotary: true,
    requiresPrice: true,
    allowsPartial: true,
  },
  amicable_partition: {
    code: 'amicable_partition',
    label: 'Partage amiable',
    description: 'Sortie d\'indivision par accord entre indivisaires',
    category: 'indivision',
    requiresNotary: true,
    requiresPrice: false,
    allowsPartial: false,
  },

  // ===== 7. MUTATIONS JUDICIAIRES ET ADMINISTRATIVES =====
  seizure: {
    code: 'seizure',
    label: 'Saisie immobilière',
    description: 'Vente forcée suite à dettes',
    category: 'judicial',
    requiresNotary: true,
    requiresPrice: true,
    allowsPartial: false,
  },
  expropriation: {
    code: 'expropriation',
    label: 'Expropriation',
    description: 'Acquisition forcée pour cause d\'utilité publique',
    category: 'judicial',
    requiresNotary: false,
    requiresPrice: true,
    allowsPartial: false,
  },

  // ===== 8. MUTATIONS SPÉCIFIQUES =====
  payment_in_kind: {
    code: 'payment_in_kind',
    label: 'Dation en paiement',
    description: 'Remise d\'un bien pour payer une dette (ex: droits de succession)',
    category: 'specific',
    requiresNotary: true,
    requiresPrice: false,
    allowsPartial: false,
  },
  other: {
    code: 'other',
    label: 'Autre',
    description: 'Autre type de mutation (à préciser dans le sous-type)',
    category: 'specific',
    requiresNotary: false,
    requiresPrice: false,
    allowsPartial: false,
  },
}
/**
 * Obtenir les métadonnées d'un type de mutation
 */
export function getMutationTypeMetadata(type: UnitMutationType): MutationTypeMetadata {
  const metadata = MUTATION_TYPE_METADATA[type]
  if (!metadata) {
    throw new Error(`Unknown mutation type: ${type}`)
  }
  return metadata
}
/**
 * Obtenir tous les types de mutation
 */
export function getAllMutationTypes(): MutationTypeMetadata[] {
  return Object.values(MUTATION_TYPE_METADATA)
}
/**
 * Obtenir tous les types de mutation d'une catégorie donnée
 */
export function getMutationTypesByCategory(
  category: MutationTypeMetadata['category']
): MutationTypeMetadata[] {
  return Object.values(MUTATION_TYPE_METADATA).filter((m) => m.category === category)
}
/**
 * Formater le nom d'un propriétaire
 */
export function formatOwnerName(owner: {
  first_name: string | null
  last_name: string | null
} | null): string {
  if (!owner) return 'Propriétaire inconnu'
  const parts = [owner.first_name, owner.last_name].filter(Boolean)
  return parts.join(' ') || 'Propriétaire inconnu'
}
/**
 * Formater une quote-part (fraction)
 */
export function formatShare(numerator: number, denominator: number): string {
  return `${numerator}/${denominator}`
}
/**
 * Formater un type de propriété
 */
export function formatOwnershipType(type: UnitMutationOwnershipType): string {
  const labels: Record<UnitMutationOwnershipType, string> = {
    full: 'Pleine propriété',
    usufruct: 'Usufruit',
    bare: 'Nue-propriété',
  }
  return labels[type] || 'Non défini'
}
/**
 * Calculer les totaux par type de propriété
 */
export interface SharesTotals {
  full: number
  usufruct: number
  bare: number
}

export function calculateSharesTotals(parties: MutationPartyInput[]): SharesTotals {
  return parties.reduce(
    (totals, party) => {
      // Vérifier que les valeurs sont valides
      if (
        party.share_numerator === undefined ||
        party.share_numerator === null ||
        party.share_denominator === undefined ||
        party.share_denominator === null ||
        party.share_denominator === 0
      ) {
        return totals
      }

      const share = party.share_numerator / party.share_denominator
      const ownershipType = party.ownership_type || 'full'

      return {
        ...totals,
        [ownershipType]: totals[ownershipType] + share,
      }
    },
    { full: 0, usufruct: 0, bare: 0 } as SharesTotals
  )
}

/**
 * Valider la cohérence des parts des nouveaux propriétaires
 * Chaque type de propriété utilisé doit totaliser exactement 1 (100%)
 */
export function validateMutationShares(
  formerParties: MutationPartyInput[],
  newParties: MutationPartyInput[]
): { valid: boolean; error?: string; formerTotals: SharesTotals; newTotals: SharesTotals } {
  const formerTotals = calculateSharesTotals(formerParties)
  const newTotals = calculateSharesTotals(newParties)

  // Tolérance de 0.0001 pour les erreurs d'arrondi
  const tolerance = 0.0001

  // Vérifier que chaque type de propriété utilisé totalise 1
  // Si un type est utilisé (> 0), il doit totaliser exactement 1

  // Vérifier la pleine propriété
  if (newTotals.full > 0 && Math.abs(newTotals.full - 1) > tolerance) {
    return {
      valid: false,
      error: `Les parts en pleine propriété doivent totaliser 1.0000 (actuellement ${newTotals.full.toFixed(4)})`,
      formerTotals,
      newTotals,
    }
  }

  // Vérifier l'usufruit
  if (newTotals.usufruct > 0 && Math.abs(newTotals.usufruct - 1) > tolerance) {
    return {
      valid: false,
      error: `Les parts en usufruit doivent totaliser 1.0000 (actuellement ${newTotals.usufruct.toFixed(4)})`,
      formerTotals,
      newTotals,
    }
  }

  // Vérifier la nue-propriété
  if (newTotals.bare > 0 && Math.abs(newTotals.bare - 1) > tolerance) {
    return {
      valid: false,
      error: `Les parts en nue-propriété doivent totaliser 1.0000 (actuellement ${newTotals.bare.toFixed(4)})`,
      formerTotals,
      newTotals,
    }
  }

  // Vérifier qu'il y a au moins un type de propriété défini
  if (newTotals.full === 0 && newTotals.usufruct === 0 && newTotals.bare === 0) {
    return {
      valid: false,
      error: 'Aucune part définie pour les nouveaux propriétaires',
      formerTotals,
      newTotals,
    }
  }

  return { valid: true, formerTotals, newTotals }
}
